import { EXAMPLES_FILTER_LABEL, SCORING_FILTER_LABEL } from '@/lib/criterion-labels';
import { CRITERION_WORDING } from '@/lib/criterion-wording';
import type {
  VersionedCounts,
  VersionedFilterChip,
  VersionedListSpec,
  VersionedSearch,
} from '@/lib/versioned-filters';
import {
  activeVersionedFilters,
  filterVersioned,
  hasActiveVersionedFilters,
  hasHiddenVersionedFilter,
  readVersionedFilters,
  resetVersionedFilters,
  versionedAuthors,
  versionedCountLabel,
  versionedCounts,
  versionedFiltersOf,
  versionedSearchOf,
} from '@/lib/versioned-filters';
import type { CriterionCard, CriterionFilters } from '@matter_extractor/shared';
import {
  CRITERION_FEW_EXAMPLES_MAX,
  CRITERION_FILTERS_DEFAULT,
  criterionExamplesFilterSchema,
  criterionScoringFilterSchema,
} from '@matter_extractor/shared';

/**
 * What the Critères list adds to the common rule set of §4 (refonte modulaire
 * §6).
 *
 * Thirteen of the filters, the four sorts, the sparse URL, the chips and the
 * counting are in `versioned-filters.ts`, and every versioned object gets them.
 * A criterion adds THREE dimensions of its own. Two of them count the same
 * thing - the number of positive examples that feed the scribe: the filter
 * « Exemples » and the sort of the same name. The third is « Notation », which
 * filters and does NOT sort (§6): a scale is a shape and not a quantity, so an
 * order on it would rank criteria on a number that says nothing.
 *
 * They are declared as a `VersionedListSpec`, which is the whole contract the
 * layer reads: the neutral state, how to read and write the object's own
 * dimensions in the URL, whether a card survives them, the chips that undo
 * them, and the comparators the four common sorts do not cover. Nothing else
 * about a criterion reaches the filtering.
 */

/** The criterion's first own filter (§6): « aucun », « 1 à 4 », « 5 et plus ». */
function matchesExamples(card: CriterionCard, filters: CriterionFilters): boolean {
  switch (filters.examples) {
    case 'all':
      return true;
    case 'none':
      return card.exampleCount === 0;
    case 'few':
      return card.exampleCount >= 1 && card.exampleCount <= CRITERION_FEW_EXAMPLES_MAX;
    case 'many':
      return card.exampleCount > CRITERION_FEW_EXAMPLES_MAX;
  }
}

/**
 * The criterion's second own filter (§6): scored, not scored, or both.
 *
 * Read off the DEVELOPMENT half of `scoring`, whose `null` is the whole answer -
 * a scored criterion has at least two bands, so there is no count to compare
 * against and no threshold to agree on with the card. Development because the
 * list of §6 shows the development version, and « Notés » has to select the
 * cards the mention « noté · 5 tranches » appears on.
 */
function matchesScoring(card: CriterionCard, filters: CriterionFilters): boolean {
  switch (filters.scoring) {
    case 'all':
      return true;
    case 'scored':
      return card.scoring.development !== null;
    case 'unscored':
      return card.scoring.development === null;
  }
}

export const CRITERION_LIST_SPEC: VersionedListSpec<CriterionCard, CriterionFilters> = {
  defaults: CRITERION_FILTERS_DEFAULT,

  read: (search) => {
    const filters: Partial<CriterionFilters> = {};
    const examples = criterionExamplesFilterSchema.safeParse(search.examples);
    if (examples.success && examples.data !== CRITERION_FILTERS_DEFAULT.examples) {
      filters.examples = examples.data;
    }
    const scoring = criterionScoringFilterSchema.safeParse(search.scoring);
    if (scoring.success && scoring.data !== CRITERION_FILTERS_DEFAULT.scoring) {
      filters.scoring = scoring.data;
    }
    return filters;
  },

  write: (filters) => {
    const search: Partial<CriterionFilters> = {};
    if (filters.examples !== CRITERION_FILTERS_DEFAULT.examples) search.examples = filters.examples;
    if (filters.scoring !== CRITERION_FILTERS_DEFAULT.scoring) search.scoring = filters.scoring;
    return search;
  },

  matches: (card, filters) => matchesExamples(card, filters) && matchesScoring(card, filters),

  chips: (filters): VersionedFilterChip<CriterionFilters>[] => {
    const chips: VersionedFilterChip<CriterionFilters>[] = [];
    if (filters.examples !== 'all') {
      chips.push({
        id: 'examples',
        label: `Exemples : ${EXAMPLES_FILTER_LABEL[filters.examples].toLowerCase()}`,
        patch: { examples: 'all' },
      });
    }
    if (filters.scoring !== 'all') {
      chips.push({
        id: 'scoring',
        label: `Notation : ${SCORING_FILTER_LABEL[filters.scoring].toLowerCase()}`,
        patch: { scoring: 'all' },
      });
    }
    return chips;
  },

  sorts: {
    /*
      The most furnished first, which is what an operator sorting on examples is
      looking for. The tie is broken BY NAME by the layer, so this comparator
      answers 0 on an equal count rather than inventing a second measure.
    */
    examples: (a, b) => b.exampleCount - a.exampleCount,
  },
};

/* ------------------------------------------------- the layer, bound to §6 */

/**
 * The layer's rules with the criterion's spec already supplied.
 *
 * The same shape as the ten named calls of `criteriaApi`: a factory plus a set
 * of bindings, so a caller writes `filterCriteria(cards, filters)` and not
 * `filterVersioned(cards, filters, CRITERION_LIST_SPEC)` eight times on one
 * screen. The spec is the contract; these are the reader's version of it, and
 * they are what the route and the pure tests call.
 */

export type CriterionSearch = VersionedSearch<CriterionFilters>;
export type { VersionedCounts as CriterionCounts };

export const readCriterionFilters = (search: Record<string, unknown>): CriterionSearch =>
  readVersionedFilters(search, CRITERION_LIST_SPEC);

export const criterionFiltersOf = (search: CriterionSearch): CriterionFilters =>
  versionedFiltersOf(search, CRITERION_LIST_SPEC);

export const criterionSearchOf = (filters: CriterionFilters): CriterionSearch =>
  versionedSearchOf(filters, CRITERION_LIST_SPEC);

export const filterCriteria = (
  cards: readonly CriterionCard[],
  filters: CriterionFilters,
): CriterionCard[] => filterVersioned(cards, filters, CRITERION_LIST_SPEC);

export const criterionCounts = (
  cards: readonly CriterionCard[],
  filters: CriterionFilters,
): VersionedCounts => versionedCounts(cards, filters, CRITERION_LIST_SPEC);

/** « 6 critères » with nothing filtered, « 2 critères sur 6 » with something (§4). */
export const criterionCountLabel = (counts: VersionedCounts): string =>
  versionedCountLabel(counts, CRITERION_WORDING.noun);

export const criterionAuthors = versionedAuthors;

export const activeCriterionFilters = (
  filters: CriterionFilters,
): VersionedFilterChip<CriterionFilters>[] => activeVersionedFilters(filters, CRITERION_LIST_SPEC);

export const hasActiveCriterionFilters = (filters: CriterionFilters): boolean =>
  hasActiveVersionedFilters(filters, CRITERION_LIST_SPEC);

export const hasHiddenCriterionFilter = (filters: CriterionFilters): boolean =>
  hasHiddenVersionedFilter(filters, CRITERION_LIST_SPEC);

export const resetCriterionFilters = (filters: CriterionFilters): CriterionFilters =>
  resetVersionedFilters(filters, CRITERION_LIST_SPEC);

export { usageTotal } from '@/lib/versioned-filters';
